import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './config';
import { auditLogService } from '@/services/auditLogService';
import { sanitizeFileName } from '@/utils/fileNameSanitizer';

export interface FloorMapPosition {
  x: number; // 0-1, 0 = left, 1 = right
  y: number; // 0-1, 0 = top, 1 = bottom
}

export interface Floor {
  id?: string;
  locationId: string;
  name: string;
  floorMapImageUrl?: string;
  organizationId?: string;
  order: number; // Display order (0 = ground, 1 = 1st floor, etc.)
  createdAt?: any;
  updatedAt?: any;
}

const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(removeUndefined);
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) cleaned[key] = removeUndefined(obj[key]);
    }
    return cleaned;
  }
  return obj;
};

export const floorService = {
  // Get all floors for a location
  getFloorsByLocation: async (locationId: string, organizationId?: string) => {
    try {
      const floorsRef = collection(db, 'floors');
      let q = query(
        floorsRef,
        where('locationId', '==', locationId)
      );
      if (organizationId) {
        q = query(q, where('organizationId', '==', organizationId));
      }
      const snapshot = await getDocs(q);
      const floors: Floor[] = [];
      snapshot.forEach((d) => {
        floors.push({ id: d.id, ...d.data() } as Floor);
      });
      floors.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      return { success: true, data: floors };
    } catch (error: any) {
      console.error('Error getting floors:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single floor by ID
  getFloor: async (id: string) => {
    try {
      const floorRef = doc(db, 'floors', id);
      const snap = await getDoc(floorRef);
      if (snap.exists()) {
        return { success: true, data: { id: snap.id, ...snap.data() } as Floor };
      }
      return { success: false, error: 'Floor not found' };
    } catch (error: any) {
      console.error('Error getting floor:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a new floor
  addFloor: async (
    floorData: Omit<Floor, 'id'>,
    organizationId?: string,
    userInfo?: { id: string; name: string; email: string }
  ) => {
    try {
      const floorsRef = collection(db, 'floors');
      const cleanedData = removeUndefined({
        ...floorData,
        ...(organizationId && { organizationId }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const docRef = await addDoc(floorsRef, cleanedData);

      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'floor_added',
          entityType: 'floor',
          entityId: docRef.id,
          entityName: floorData.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId,
          details: { locationId: floorData.locationId },
        });
      }

      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding floor:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a floor
  updateFloor: async (
    id: string,
    floorData: Partial<Floor>,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      const floorRef = doc(db, 'floors', id);
      const cleanedData = removeUndefined({
        ...floorData,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(floorRef, cleanedData);

      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'floor_updated',
          entityType: 'floor',
          entityId: id,
          entityName: floorData.name ?? 'Floor',
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId,
        });
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error updating floor:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a floor
  deleteFloor: async (
    id: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      const floorResult = await floorService.getFloor(id);
      if (!floorResult.success || !floorResult.data) {
        return { success: false, error: 'Floor not found' };
      }
      const floor = floorResult.data;

      // Delete floor map image from Storage if exists
      if (floor.floorMapImageUrl) {
        try {
          const urlPath = new URL(floor.floorMapImageUrl).pathname;
          const storagePath = decodeURIComponent(urlPath.split('/o/')[1]?.split('?')[0] || '');
          if (storagePath) {
            const storageRef = ref(storage, storagePath);
            await deleteObject(storageRef);
          }
        } catch (storageErr) {
          console.warn('Could not delete floor map image from storage:', storageErr);
        }
      }

      const floorRef = doc(db, 'floors', id);
      await deleteDoc(floorRef);

      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'floor_deleted',
          entityType: 'floor',
          entityId: id,
          entityName: floor.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId,
        });
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error deleting floor:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload floor map image
  uploadFloorMapImage: async (
    floorId: string,
    file: File,
    organizationId: string
  ): Promise<{ success: boolean; url?: string; error?: string }> => {
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const safeName = sanitizeFileName(file.name.replace(/\.[^/.]+$/, '')) || 'floor-map';
      const storagePath = `organizations/${organizationId}/floors/${floorId}/floorMap_${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, {
        contentType: file.type || (ext === 'png' ? 'image/png' : 'image/jpeg'),
      });
      const url = await getDownloadURL(storageRef);
      return { success: true, url };
    } catch (error: any) {
      console.error('Error uploading floor map:', error);
      return { success: false, error: error.message };
    }
  },
};
